<script setup lang="ts">
const t2 = useT2({
  'B2B accounts only': 'B2B klientams',
})

export type Updaters = {
  remove(): void
  update(i: number): void
}

export type Props = {
  disabled: boolean
  slug: string | None
  uiPrice: gen.ProductListingBoxUiPriceFragment | None

  notify: ReturnType<typeof useNotify>
  settingsQty: gen.ProductSettingsQtyFragment | None
  //
  size: 's' | 'm' | 'l'
  sizeWMax?: boolean
  streach?: boolean
  stock: number | None
  //
  qty: number
  qtySet(qty: number): void

  availability: gen.ProductAvailabilityType | None
  privateVisibility: boolean | None
}

const props = defineProps<Props>()

function saveQtyAuto(qty: number): void {
  if (qty !== props.qty) props.qtySet(qty)
}

const ui = computed(() => {
  const { stock } = props
  const min = helpers.toPositive(props.settingsQty?.min, 1)
  return {
    min,
    inStock: helpers.isDef(stock) && stock >= min,
    max: helpers.toNumber(props.stock ?? props.settingsQty?.max),
    start: min,
  }
})

const isNew = computed(() => !props.qty)

const qtyCurr = computed(() => props.qty || ui.value.start)
const pricing = computed(() => {
  const uiPrice = props.uiPrice
  const qty = qtyCurr.value
  let priceUnit = helpers.toPositive(uiPrice?.price?.number, 0)
  let activeQty = -1

  uiPrice?.bulk?.forEach((item) => {
    const lineItemsEq = uiPrice.lineItems.some((i) => i.price.number === item.price)
    if (qty >= item.qty && item.qty >= activeQty && (priceUnit >= item.price || lineItemsEq)) {
      activeQty = item.qty
      priceUnit = item.price
    }
  })
  return { priceUnit, activeQty, total: helpers.priceMx(priceUnit, qty), bulk: uiPrice?.bulk, qty }
})

const disabled2 = computed(() => !ui.value.inStock || props.disabled)
const out = computed(() => qtyCurr.value > ui.value.max)

const css = useCssModule()
const labelCss = computed(() => (props.size === 's' ? '_ws_nowrap _fw-xl _fs09' : '_fw-xl _fs09'))

const cssCont = computed(() => {
  const { sizeWMax, streach } = props
  return ['_row _inline _ctrY _gap1', isNew.value && css.new, streach && css.streach, sizeWMax && css.wMax, out.value && css.out]
})

const cssVards = computed(() => {
  const s = props.size
  if (s === 's') return '--h: 40px; --w: 120px; --svg: 20px;'
  if (s === 'm') return '--w: 170px; --h: 44px;'
  return '--h: 50px;  --w: 180px'
})

const init3 = useInited('init3')

const notInStock = computed(() => {
  return !props.uiPrice?.price && props.privateVisibility ? t2('B2B accounts only') : props.settingsQty?.stockMsg === 'Soon' ? t2('Available soon') : t2('Sold out')
})
</script>

<template>
  <div :class="cssCont" v-bind="$attrs" :style="cssVards">
    <UiNotify v-if="init3" :notify />

    <slot name="before" />

    <template v-if="availability === 'Upcoming'">
      <LazyProductUpcomingUi v-if="init3" :product-slug="slug" :size />
    </template>

    <div v-else :ref="notify.elem" :class="[$style.btn, disabled2 && $style.disabled]" style="height: var(--h); width: var(--w)">
      <button v-if="isNew" type="button" aria-label="Add" :disabled="disabled2" :class="$style.value" @click.passive="() => saveQtyAuto(ui.start)">
        <span v-if="!ui.inStock" class="_flex _fw-xl _fs1 _txt_ctr">{{ notInStock }}</span>
        <template v-else>
          <span class="_svg _ctr">
            <LazyIconCart v-if="init3" class="_svg" />
          </span>
          <span :class="[$style.col, streach ? '_gap1' : '_gap02', size === 's' && '_pt02']">
            <span :class="labelCss">{{ t2('Add to cart') }}</span>
            <UiPriceCounter v-if="size !== 's' && pricing.total" :price="pricing.total" :class="$style.price" />
          </span>
        </template>
      </button>

      <ProductBtnBuyQty
        v-else
        :max="ui.max"
        :disabled
        :out
        :stock
        :size
        :settings-qty
        :in-stock="ui.inStock"
        :min="ui.min"
        :start="ui.start"
        :price-unit="pricing.priceUnit"
        :total="pricing.total"
        :value="qtyCurr"
        :value-set="saveQtyAuto"
        :bulk="uiPrice?.bulk"
      />
    </div>

    <slot />
  </div>

  <LazyProductBulkPrice v-if="init3 && availability !== 'Upcoming' && uiPrice?.bulk?.length" :pricing :qty :stock :ui-price :settings-qty :qty-set :size-small="size !== 'l'" />
</template>

<style module>
.btn {
  composes: global(_relative);
  composes: global(_row);
  composes: global(_fs09);
  composes: global(_ls-xs);
  composes: global(_ctrY);
  composes: global(_br99);
  composes: global(_wmax);
  composes: global(_noflex);
  composes: global(_c1);

  --b1: var(--bg);
  --b2: var(--black-5);
  background: var(--b1);
  border: 1px solid var(--b2);
}

.new .btn {
  --b1: var(--blue3);
  --b2: var(--blue3);
}

.out .btn {
  --b2: var(--red);
}

.new .btn:not(.disabled):hover {
  --shadow: 1px 2px 0 hsla(240, 4%, 66%, 0.1);
  --b1: var(--blue2);
  --b2: var(--blue2);
}

.new .btn {
  color: var(--bg);
}

.streach {
  composes: global(_w100p);
  composes: global(_flexblock);
  --w: auto;
}

.streach .btn {
  flex: 1 1 auto;
}

.value {
  composes: global(_row);
  composes: global(_ctrY);
  composes: global(_gap04);
  composes: global(_pr14);
  composes: global(_pl1);
  composes: global(_h100p);

  composes: global(_br99);

  composes: global(_flex);
  composes: global(_spaced);

  box-shadow: var(--shadow, none);
}

.col {
  composes: global(_col);
  composes: global(_endA);
}

.streach .col {
  justify-content: center;
  flex: 1 1 auto;
  flex-direction: row;
}

.price {
  composes: global(_fs09);
  color: var(--black-6);
}

.wMax {
  --w: max-content;
}

.btn.disabled {
  color: var(--black-4);
  cursor: not-allowed;
  --b1: var(--black-6);
  --b2: var(--black-5);
}
</style>
